/**
 * Admin — Payout Accounts KYC verification panel — /admin/payout-accounts
 * Platform admin reviews pending KYC submissions, previews docs,
 * verifies / requests info / rejects.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, BadgeCheck, MessageCircleWarning, XCircle,
  Landmark, IdCard, FileCheck2, FileText, Files, Mail,
} from "lucide-react";

const STATUSES = ["pending", "info_requested", "verified", "rejected"];
const STATUS_LABEL = {
  pending: "Pending", info_requested: "Info needed",
  verified: "Verified", rejected: "Rejected",
};
const STATUS_COLOR = {
  pending: "#F5B82E", info_requested: "#F59E0B",
  verified: "#22C55E", rejected: "#EF4444",
};

const DOC_META = {
  pan_card:                 { icon: IdCard,     label: "PAN Card" },
  registration_certificate: { icon: FileText,   label: "Registration Cert" },
  cancelled_cheque:         { icon: FileCheck2, label: "Cancelled Cheque" },
  board_resolution:         { icon: Files,      label: "Board Resolution" },
};

const ENTITY_TINT = {
  union:    "#EC4899",
  studio:   "#0891B2",
  vendor:   "#F97316",
  producer: "#7C3AED",
  agency:   "#10B981",
};

export default function AdminPayoutAccounts() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("pending");
  const [rows, setRows]   = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setL]   = useState(true);
  const [action, setAction] = useState(null);   // {row, kind}
  const [note, setNote]   = useState("");
  const [saving, setSaving] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewMime, setPreviewMime] = useState(null);
  const [detail, setDetail] = useState(null);   // full row with unmasked bank

  async function load() {
    setL(true);
    try {
      const { data } = await api.get(`/admin/payout-accounts`, { params: { status } });
      setRows(data.payout_accounts);
      setCounts(data.counts);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        toast.error("Admin access required"); nav("/"); return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed");
    } finally { setL(false); }
  }
  useEffect(() => { if (!authLoading && user) void load(); /* eslint-disable-next-line */ }, [status, authLoading, user]);

  useEffect(() => () => {
    if (previewSrc?.startsWith("blob:")) URL.revokeObjectURL(previewSrc);
  }, [previewSrc]);

  if (authLoading) return null;
  if (!user) return null;

  async function openPreview(rowId, docKey) {
    try {
      const { data, headers } = await api.get(
        `/admin/payout-accounts/${rowId}/doc/${docKey}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPreviewSrc(url);
      setPreviewMime(headers["content-type"] || "");
    } catch {
      toast.error("Could not load document");
    }
  }

  async function openDetail(rowId) {
    try {
      const { data } = await api.get(`/admin/payout-accounts/${rowId}`);
      setDetail(data);
    } catch {
      toast.error("Could not load details");
    }
  }

  async function submitAction() {
    if (!action) return;
    setSaving(true);
    try {
      if (action.kind === "verify") {
        await api.post(`/admin/payout-accounts/${action.row.id}/verify`, { admin_note: note });
        toast.success("Verified — account can now receive marketplace payouts");
      } else if (action.kind === "request_info") {
        if (note.trim().length < 3) throw new Error("Describe what's missing.");
        await api.post(`/admin/payout-accounts/${action.row.id}/request-info`, { admin_note: note });
        toast.success("Info request sent");
      } else if (action.kind === "reject") {
        if (note.trim().length < 3) throw new Error("Add a rejection reason.");
        await api.post(`/admin/payout-accounts/${action.row.id}/reject`, { reason: note });
        toast.success("Rejected");
      }
      setAction(null); setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="admin-payout-accounts">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="admin-payouts-back">
          <ArrowLeft size={12} /> Home
        </Link>
        <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-emerald-300">
          Onboarding · Payout KYC
        </div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Payout Accounts</h1>
        <div className="mt-1 text-sm text-white/55">
          Verify PAN, cancelled cheque, registration + board resolution
          before an account can receive marketplace payouts.
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-payout-tabs">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.24em] border ${status === s ? "border-white/50 text-white bg-white/5" : "border-white/10 text-white/55 hover:border-white/25"}`}
              data-testid={`admin-payout-tab-${s}`}>
              {STATUS_LABEL[s]}
              <span className="ml-2 tabular-nums" style={{ color: STATUS_COLOR[s] }}>
                {counts[s] || 0}
              </span>
            </button>
          ))}
        </div>

        {loading && <div className="mt-10 text-center text-white/40 uppercase tracking-[0.3em] text-xs">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="mt-10 text-center py-10 text-white/45" data-testid="admin-payout-empty">
            No payout accounts in this status.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="admin-payout-list">
            {rows.map((r) => {
              const tint = ENTITY_TINT[r.entity_type] || "#4A4E51";
              return (
                <div key={r.id} className="rounded-2xl border border-white/10 p-5"
                  style={{ borderLeft: `3px solid ${tint}` }}
                  data-testid={`admin-payout-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-display text-lg uppercase tracking-tight"
                        style={{ color: tint }}>
                        {r.entity_name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-0.5">
                        {r.entity_type} · Legal: {r.legal_name} · PAN {r.pan}
                      </div>
                      <div className="mt-2 text-xs text-white/70 space-x-4">
                        <span className="inline-flex items-center gap-1"><Landmark size={11} /> {r.bank_name}</span>
                        <span>A/C {r.account_masked}</span>
                        <span>IFSC {r.ifsc}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Object.entries(DOC_META).map(([key, { icon: Icon, label }]) => {
                          const uploaded = r.documents_uploaded?.includes(key);
                          return (
                            <button key={key}
                              onClick={() => uploaded && openPreview(r.id, key)}
                              disabled={!uploaded}
                              className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border ${uploaded ? "border-white/15 hover:border-emerald-400 hover:text-emerald-300" : "border-red-400/30 text-red-300/60"}`}
                              data-testid={`admin-payout-doc-${r.id}-${key}`}>
                              <Icon size={11} /> {label} {uploaded ? "" : "— MISSING"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>

                  {(r.kyc_status === "pending" || r.kyc_status === "info_requested") && (
                    <div className="mt-4 flex gap-2 flex-wrap">
                      <Button onClick={() => { setAction({row: r, kind: "verify"}); setNote(""); }}
                        className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4"
                        data-testid={`admin-payout-verify-${r.id}`}>
                        <BadgeCheck size={13} className="mr-1.5" /> Verify
                      </Button>
                      <Button variant="outline"
                        onClick={() => { setAction({row: r, kind: "request_info"}); setNote(""); }}
                        className="rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4 border-amber-400/50 text-amber-300 hover:bg-amber-400/10"
                        data-testid={`admin-payout-info-${r.id}`}>
                        <MessageCircleWarning size={13} className="mr-1.5" /> Request info
                      </Button>
                      <Button variant="outline"
                        onClick={() => { setAction({row: r, kind: "reject"}); setNote(""); }}
                        className="rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4 border-red-400/50 text-red-300 hover:bg-red-400/10"
                        data-testid={`admin-payout-reject-${r.id}`}>
                        <XCircle size={13} className="mr-1.5" /> Reject
                      </Button>
                      <Button variant="outline"
                        onClick={() => openDetail(r.id)}
                        className="rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4 border-white/15 text-white/70 hover:border-white/40"
                        data-testid={`admin-payout-detail-${r.id}`}>
                        <Mail size={13} className="mr-1.5" /> Details
                      </Button>
                    </div>
                  )}

                  {r.admin_note && (
                    <div className="mt-3 text-xs text-white/60 border-l-2 border-white/15 pl-3">
                      <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mb-1">Last note</div>
                      {r.admin_note}
                    </div>
                  )}
                  {r.rejection_reason && (
                    <div className="mt-3 text-xs text-red-200 border-l-2 border-red-400/40 pl-3">
                      <div className="text-[9px] uppercase tracking-[0.22em] text-red-300/70 mb-1">Rejection reason</div>
                      {r.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!previewSrc} onOpenChange={(v) => { if (!v) { setPreviewSrc(null); setPreviewMime(null); } }}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-4xl h-[80vh]" data-testid="payout-doc-preview">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Document preview</DialogTitle>
          </DialogHeader>
          {previewSrc && previewMime?.startsWith("image/") ? (
            <img src={previewSrc} alt="doc" className="max-w-full max-h-[60vh] mx-auto object-contain rounded" />
          ) : previewSrc ? (
            <iframe src={previewSrc} className="w-full h-[60vh] rounded bg-white" title="doc" />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-md" data-testid="payout-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{detail?.entity_name}</DialogTitle>
            <DialogDescription className="text-white/55">Full details for verification.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <Row k="Legal" v={detail.legal_name} />
              <Row k="PAN" v={detail.pan} />
              <Row k="Owner email" v={detail.owner_email} />
              <Row k="Account holder" v={detail.account_holder} />
              <Row k="Bank" v={detail.bank_name} />
              <Row k="Account #" v={detail.account_number} />
              <Row k="IFSC" v={detail.ifsc} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action dialog */}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setNote(""); } }}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-lg" data-testid="admin-payout-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">
              {action?.kind === "verify"       && `Verify ${action.row.entity_name}`}
              {action?.kind === "request_info" && `Request info on ${action.row.entity_name}`}
              {action?.kind === "reject"       && `Reject ${action?.row.entity_name}`}
            </DialogTitle>
            <DialogDescription className="text-white/55">
              {action?.kind === "verify" && "Optional note. The account holder will see this on their /payout-account page."}
              {action?.kind === "request_info" && "Describe exactly what's missing so the account holder can resubmit."}
              {action?.kind === "reject" && "Reason will be shown to the holder. They can resubmit as a fresh payout account."}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder={action?.kind === "verify"
              ? "Optional — 'Verified via cancelled cheque + PAN + reg cert.'"
              : action?.kind === "request_info"
              ? "e.g. Please share a clearer cancelled cheque with IFSC printed."
              : "e.g. PAN card does not match legal name on registration certificate."}
            className="bg-black/40 border-white/15 min-h-[120px]"
            data-testid="admin-payout-action-note" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAction(null); setNote(""); }}>Cancel</Button>
            <Button onClick={submitAction} disabled={saving}
              className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
              data-testid="admin-payout-action-submit">
              {saving ? (<><Loader2 size={12} className="animate-spin mr-2" /> Saving…</>) : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{k}</div>
      <div className="text-white/85 text-sm truncate">{v || "—"}</div>
    </div>
  );
}
